import { useMemo, useRef } from 'react';
import clsx from 'clsx';
import { X, Upload } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { TATTOO_STYLES } from '@/domain/types';
import type { CustomerRecord, DesignIntent, SessionLength, StudioId } from '@/domain/types';
import { artistMatchesStyle } from '@/domain/availabilityScope';
import { inspirationFor, buildReferenceGroups, type ReferenceCandidate } from '@/utils/imageHelpers';
import { simDateShort } from '@/utils/format';
import ReferencePicker from '@/components/ReferencePicker';
import { pieceSessionLabel } from '@/utils/pieceHelpers';
import ArtistPicker from '@/components/booking/ArtistPicker';
import PlacementFields, { type PlacementValue } from '@/components/booking/PlacementFields';
import { AutosaveTextInput, AutosaveTextarea } from '@/components/AutosaveTextInput';

const SIZE_OPTIONS: Array<{ value: SessionLength; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'half-day', label: 'Half day' },
  { value: 'full-day', label: 'Full day' },
];

// How many sittings - that is the only question these two answer. Continuing an
// existing piece is a different question, asked on the past-bookings rail where
// the history the agent is choosing from already lives.
const PIECE_MODES: Array<{ key: 'new' | 'one-off'; label: string; hint: string }> = [
  { key: 'new', label: 'Multiple sessions', hint: 'name it' },
  { key: 'one-off', label: 'One-off session', hint: '1 session' },
];

interface Props {
  customer: CustomerRecord;
  /** Namespaces data-testids per host ("qwm" in My Queue, "customer-brief" on the record). */
  idPrefix: string;
}

/**
 * The full segmented booking brief, shared by My Queue and the customer record's
 * left rail: Piece -> Style & artist -> Placement & look -> References. Every
 * field persists to the brain store (customer prefs + design intent), Style /
 * artist / studio scope the availability pane (best-fit), and the whole brief
 * flows onto the held draft booking when the deposit is sent.
 */
export default function CustomerBriefControls({ customer, idPrefix }: Props) {
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const pieces = useBrainStore((s) => s.pieces);
  const bookings = useBrainStore((s) => s.bookings);
  const setCustomerPreferences = useBrainStore((s) => s.setCustomerPreferences);
  const updateDesignIntent = useBrainStore((s) => s.updateDesignIntent);

  const customerId = customer.id;
  const di = customer.designIntent;
  const pieceMode = di?.pieceMode ?? 'one-off';
  // Memoised because the reference partition depends on it; a fresh [] each render
  // would recompute the whole pool every time.
  const selectedRefs = useMemo(() => di?.referencePhotos ?? [], [di?.referencePhotos]);

  const studioForArtists = customer.preferredStudioId ?? customer.primaryStudioId;
  const studioArtists = useMemo(
    () =>
      Object.values(artists)
        .filter((a) => a.studioId === studioForArtists && !a.archived)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artists, studioForArtists],
  );

  // Style narrows the primary-artist list to matching specialists (falls back to
  // the full roster when nothing matches, so the field never dead-ends).
  const filteredArtists = useMemo(() => {
    if (!customer.preferredStyle) return studioArtists;
    const matches = studioArtists.filter((a) => artistMatchesStyle(a, customer.preferredStyle!));
    return matches.length > 0 ? matches : studioArtists;
  }, [studioArtists, customer.preferredStyle]);

  // Set from the past-bookings rail, not from here. Resolved so the brief can
  // show what is being continued and offer the one way back out.
  const continuedPiece =
    pieceMode === 'continue' && di?.continuePieceId ? pieces[di.continuePieceId] : undefined;

  const customerBookings = useMemo(
    () =>
      customer.bookings
        .map((id) => bookings[id])
        .filter((b): b is NonNullable<typeof b> => Boolean(b)),
    [customer.bookings, bookings],
  );

  /**
   * The pool of pictures, accumulated and never shrunk.
   *
   * `designIntent.referencePhotos` is this screen's SELECTION, and `inspirationFor`
   * reads it as a pool source - so deselecting a photo used to delete it from the
   * grid outright, and a photo that was both selected and a thread attachment
   * changed sort key as it was ticked, which is why tiles jumped around. Sticky
   * accumulation keeps every url that has ever been in scope for this customer,
   * so selecting is purely a selection.
   */
  const poolRef = useRef<{ customerId: string; byUrl: Map<string, ReferenceCandidate> }>({
    customerId,
    byUrl: new Map(),
  });
  if (poolRef.current.customerId !== customerId) {
    poolRef.current = { customerId, byUrl: new Map() };
  }
  for (const p of inspirationFor(customer)) {
    const existing = poolRef.current.byUrl.get(p.url);
    // Prefer a dated (thread-sourced) record over an undated brief one, so arrival
    // order stays stable regardless of what happens to be selected.
    if (!existing || (!existing.capturedAt && p.capturedAt)) {
      poolRef.current.byUrl.set(p.url, { url: p.url, caption: p.caption, capturedAt: p.capturedAt });
    }
  }
  for (const b of customerBookings) {
    for (const url of b.referencePhotos ?? []) {
      if (!poolRef.current.byUrl.has(url)) poolRef.current.byUrl.set(url, { url });
    }
  }
  const pool = [...poolRef.current.byUrl.values()];

  // Hidden-by-history, not by selection - see buildReferenceGroups.
  const references = useMemo(
    () =>
      buildReferenceGroups({
        photos: pool,
        bookings: customerBookings,
        labelForBooking: (b) => simDateShort(b.start),
      }),
    // pool is a fresh array each render by design (sticky accumulation); its
    // contents only grow, so key the memo on size + the bookings it groups against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pool.length, customerBookings],
  );

  const onSelectRefs = (urls: string[]) =>
    updateDesignIntent(customerId, { referencePhotos: urls });

  // One-off is still a named piece; it just defaults to a single estimated
  // session so it reports as a one-off.
  const setPieceMode = (mode: 'new' | 'one-off') =>
    updateDesignIntent(
      customerId,
      mode === 'one-off' ? { pieceMode: mode, estimatedSessions: 1 } : { pieceMode: mode },
    );

  // Drops out of a continuation back to the two session modes. Clears the piece
  // link and the fields the rail filled in, so the brief doesn't keep a stale
  // title for a piece nobody is continuing any more.
  const clearContinuation = () =>
    updateDesignIntent(customerId, {
      pieceMode: 'new',
      continuePieceId: undefined,
      newPieceTitle: undefined,
      estimatedSessions: undefined,
    });

  // Upload a new reference from the device (client-side data URL) into the brief.
  const fileRef = useRef<HTMLInputElement>(null);
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        updateDesignIntent(customerId, { referencePhotos: [...selectedRefs, reader.result] });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const placementValue: PlacementValue = {
    bodyPart: di?.bodyPart,
    bodyPartSide: di?.bodyPartSide,
    bodyPartQualifier: di?.bodyPartQualifier,
    placementNote: di?.placement ?? '',
  };
  const onPlacementChange = (patch: Partial<PlacementValue>) => {
    const { placementNote, ...rest } = patch;
    const next: Partial<DesignIntent> = { ...rest };
    if (placementNote !== undefined) next.placement = placementNote;
    updateDesignIntent(customerId, next);
  };

  const inputCls =
    'w-full rounded-md border border-gold-dim/30 bg-ink-black px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none';
  const pill = (active: boolean) =>
    clsx(
      'px-2.5 py-1 rounded-full text-[11px] border transition',
      active
        ? 'border-gold-bright bg-gold/10 text-gold-bright'
        : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
    );

  return (
    <>
      {/* 1 · Piece */}
      <section data-testid={`${idPrefix}-brief`}>
        <p className="label-small-caps text-gold-bright mb-2">Piece</p>

        {/* Continuing an existing piece is picked on the past-bookings rail, so
            here it reads back as a fact with one way out, not as a third mode. */}
        {continuedPiece ? (
          <div
            className="rounded-md border border-gold-bright bg-gold/10 px-3 py-2"
            data-testid={`${idPrefix}-continuing`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-[9px] uppercase tracking-widest text-gold-bright">
                  Continuing
                </p>
                <p className="font-serif text-sm text-cream mt-0.5">
                  {pieceSessionLabel(continuedPiece)}
                </p>
              </div>
              <button
                type="button"
                onClick={clearContinuation}
                aria-label="Stop continuing this piece"
                data-testid={`${idPrefix}-continuing-clear`}
                className="shrink-0 text-cream-soft hover:text-cream transition"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5" data-testid={`${idPrefix}-piece-mode`}>
            {PIECE_MODES.map((m) => {
              const active = pieceMode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setPieceMode(m.key)}
                  data-testid={`${idPrefix}-piece-mode-${m.key}`}
                  className={clsx(
                    'rounded-md border px-2 py-1.5 text-center text-[11px] leading-tight transition',
                    active
                      ? 'border-gold-bright bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                  )}
                >
                  {m.label}
                  <span className="block text-[8px] uppercase tracking-widest text-cream-faint">
                    {m.hint}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {pieceMode === 'new' || pieceMode === 'one-off' ? (
          <AutosaveTextInput
            type="text"
            value={di?.newPieceTitle ?? ''}
            resetKey={customerId}
            onCommit={(v) => updateDesignIntent(customerId, { newPieceTitle: v })}
            data-testid={`${idPrefix}-piece-name`}
            placeholder="Piece name - e.g. Dragon sleeve"
            className={clsx(inputCls, 'mt-2')}
          />
        ) : null}

        {pieceMode === 'new' ? (
          <AutosaveTextInput
            type="number"
            min={1}
            max={20}
            value={di?.estimatedSessions != null ? String(di.estimatedSessions) : ''}
            resetKey={customerId}
            onCommit={(v) =>
              updateDesignIntent(customerId, {
                estimatedSessions: v ? Number(v) : undefined,
              })
            }
            data-testid={`${idPrefix}-piece-sessions`}
            placeholder="Estimated sessions - e.g. 4"
            className={clsx(inputCls, 'mt-2')}
          />
        ) : null}

        {pieceMode === 'one-off' ? (
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-1.5">
            One-off · 1 session (for reporting)
          </p>
        ) : null}

        <label htmlFor={`${idPrefix}-notes`} className="label-small-caps mt-4 mb-1.5 block">
          Notes
        </label>
        <AutosaveTextarea
          id={`${idPrefix}-notes`}
          rows={2}
          value={di?.notes ?? ''}
          resetKey={customerId}
          onCommit={(v) => updateDesignIntent(customerId, { notes: v })}
          data-testid={`${idPrefix}-notes`}
          placeholder="Allergy notes, reference IDs, placement caveats…"
          className={clsx(inputCls, 'resize-none')}
        />
      </section>

      {/* 2 · Style & artist */}
      <section data-testid={`${idPrefix}-preferences`}>
        <p className="label-small-caps text-gold-bright mb-2">Style &amp; artist</p>

        <p className="label-small-caps mb-1.5">Style</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-style-row`}>
          {TATTOO_STYLES.map((style) => (
            <button
              key={style}
              type="button"
              onClick={() => setCustomerPreferences(customerId, { preferredStyle: style })}
              data-testid={`${idPrefix}-style-${style}`}
              className={clsx(pill(customer.preferredStyle === style), 'capitalize')}
            >
              {style.replace('-', ' ')}
            </button>
          ))}
        </div>
        {customer.preferredStyle ? (
          <p className="font-mono text-[10px] uppercase tracking-widest text-mint mt-2">
            Filtering artists to {customer.preferredStyle.replace('-', ' ')} specialists
          </p>
        ) : null}

        <label htmlFor={`${idPrefix}-pref-studio`} className="label-small-caps mt-4 mb-1.5 block">
          Studio
        </label>
        <select
          id={`${idPrefix}-pref-studio`}
          value={customer.preferredStudioId ?? customer.primaryStudioId}
          onChange={(e) =>
            setCustomerPreferences(customerId, { preferredStudioId: e.target.value as StudioId })
          }
          data-testid={`${idPrefix}-pref-studio`}
          className={inputCls}
        >
          {Object.values(studios)
            // Active studios only (tv-2na.9.5); the current pick stays listed so
            // the controlled select never loses its value.
            .filter((s) => !s.archived || s.id === (customer.preferredStudioId ?? customer.primaryStudioId))
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>

        <p className="label-small-caps mt-4 mb-1.5">Artist</p>
        <ArtistPicker
          artists={filteredArtists}
          value={customer.preferredArtistId ?? undefined}
          onSelect={(id) => setCustomerPreferences(customerId, { preferredArtistId: id })}
          placeholder="No preference"
          testid={`${idPrefix}-pref-artist`}
        />
      </section>

      {/* 3 · Placement & look */}
      <section data-testid={`${idPrefix}-placement-look`}>
        <p className="label-small-caps text-gold-bright mb-2">Placement &amp; look</p>

        <PlacementFields value={placementValue} onChange={onPlacementChange} idPrefix={idPrefix} />

        <p className="label-small-caps mt-4 mb-1.5">Colour</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-colour-row`}>
          {(['black-and-grey', 'colour'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => updateDesignIntent(customerId, { colour: c })}
              data-testid={`${idPrefix}-colour-${c}`}
              className={pill(di?.colour === c)}
            >
              {c === 'colour' ? 'Colour' : 'Black & grey'}
            </button>
          ))}
        </div>

        <p className="label-small-caps mt-4 mb-1.5">Size</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-size-row`}>
          {SIZE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateDesignIntent(customerId, { size: opt.value })}
              data-testid={`${idPrefix}-size-${opt.value}`}
              className={pill(di?.size === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* 4 · References. One grid; photos already used on an earlier booking sit
          behind the picker's filter, each carrying the date it was used on.
          Hiding is history-only, so ticking a photo never moves it. */}
      <section data-testid={`${idPrefix}-references`}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onUpload}
          data-testid={`${idPrefix}-ref-upload-input`}
        />
        <ReferencePicker
          photos={references.photos}
          hiddenCount={references.hiddenCount}
          selected={selectedRefs}
          onChange={onSelectRefs}
          label="References"
          emptyText="Nothing on file yet - upload what they sent."
          testid={`${idPrefix}-references-picker`}
          action={
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`${idPrefix}-ref-upload`}
              className="inline-flex items-center gap-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-faint hover:text-gold-bright transition"
            >
              <Upload size={11} /> Upload
            </button>
          }
        />
        {selectedRefs.length > 0 ? (
          <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-2">
            {selectedRefs.length} selected · rides onto the booking
          </p>
        ) : null}
      </section>
    </>
  );
}
